import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, EventEmitter, HostListener, inject, Output, signal, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CacheService } from '@app/_services/cache.service';
import { getLocalDateString as getDateString } from '@app/_services/date';
import { FeatureService } from '@app/_services/features/features.service';
import { SettingsService } from '@app/_services/settings.service';
import { MoneyTrackerStore } from '@app/_stores/money-tracker.store';
import { ViewSettingsStore } from '@app/_stores/view-settings.store';
import { FocusStore } from '@app/_stores/focus.store';
import { HabitStore } from '@app/_stores/habit.store';
import { JournalStore } from '@app/_stores/journal.store';
import { SvgComponent } from '../../svg/svg.component';
import { ModalService } from '@app/_services/modal.service';
import { Router } from '@angular/router';

interface AddEntityItem {
  id: string;
  icon: string;
  labelKey: string;
  action: () => void;
  visible: boolean;
  isPremium?: boolean;
}

@Component({
  selector: 'app-mobile-add-entities-list',
  standalone: true,
  imports: [
    CommonModule,
    SvgComponent
  ],
  templateUrl: './mobile-add-entities-list.component.html',
  styleUrl: './mobile-add-entities-list.component.scss'
})
export class MobileAddEntitiesListComponent {
  @Output() itemSelected = new EventEmitter<void>();
  @Output() clickOutside = new EventEmitter<void>();
  @ViewChild('scrollContainer', { read: ElementRef }) scrollContainer!: ElementRef<HTMLElement>;

  private readonly elementRef = inject(ElementRef);
  private readonly featureService = inject(FeatureService);
  private readonly dialog = inject(MatDialog);
  readonly ss = inject(SettingsService);
  readonly cc = inject(CacheService);
  readonly viewSettings = inject(ViewSettingsStore);
  readonly focusStore = inject(FocusStore);
  readonly habitStore = inject(HabitStore);
  readonly journalStore = inject(JournalStore);
  readonly moneyStore = inject(MoneyTrackerStore);
  readonly modalService = inject(ModalService);
  private readonly router = inject(Router);

  // Control visibility of premium features (signal for computed reactivity)
  showAllFeatures = signal(false);

  // Mouse drag scrolling state
  private isDragging = false;
  private startX = 0;
  private scrollLeft = 0;
  private hasDragged = false; // Track if user actually dragged (not just clicked)

  /**
   * Build list of add entity items based on enabled features
   * Following the same pattern as entity.component.ts
   * Premium features hidden behind "more" button
   */
  addEntityItems = computed((): AddEntityItem[] => {
    const items: AddEntityItem[] = [];

    // Primary items - Flutter order: Todo, Focus, List, Note, Income, Expense

    // Todo (if enabled)
    if (this.viewSettings.showTodoFeature()) {
      items.push({
        id: 'todo',
        icon: 'todo',
        labelKey: 'common_todo',
        action: () => this.openTodo(),
        visible: true,
        isPremium: false
      });
    }

    // Focus - only show if unscheduled focus setups exist
    if (this.focusStore.enabledSetups().some(s => !s.startDate)) {
      items.push({
        id: 'focusTrainer',
        icon: 'focusTrainer',
        labelKey: 'common_focusTracker',
        action: () => this.openFocusTrainer(),
        visible: true,
        isPremium: false
      });
    }

    // Habit - only show if unscheduled habit setups exist
    if (this.habitStore.enabledSetups().some(s => !s.startDate)) {
      items.push({
        id: 'habitAction',
        icon: 'habit',
        labelKey: 'common_habit',
        action: () => this.openHabitSelect(),
        visible: true,
        isPremium: false
      });
    }

    // Journal - only show if unscheduled journal setups exist
    if (this.journalStore.enabledSetups().some(s => !s.startDate)) {
      items.push({
        id: 'journalAction',
        icon: 'journal',
        labelKey: 'common_journal',
        action: () => this.openJournalSelect(),
        visible: true,
        isPremium: false
      });
    }

    // List (if enabled)
    if (this.viewSettings.showListFeature()) {
      items.push({
        id: 'list',
        icon: 'list',
        labelKey: 'common_list',
        action: () => this.openList(),
        visible: true,
        isPremium: false
      });
    }

    // Note (if enabled)
    if (this.viewSettings.showNoteFeature()) {
      items.push({
        id: 'note',
        icon: 'note',
        labelKey: 'common_note',
        action: () => this.openNote(),
        visible: true,
        isPremium: false
      });
    }

    // Money tracker - single button that opens setup selector (matches Flutter redesign)
    if (this.moneyStore.allowedOwnerAndEditorSetups().length > 0) {
      items.push({
        id: 'moneyTracker',
        icon: 'moneyTracker',
        labelKey: 'common_moneyTracker',
        action: () => this.openMoneyTrackerSelect(),
        visible: true,
        isPremium: false
      });
    }

    // "More" button - only show if NOT showing all features
    if (!this.showAllFeatures()) {
      items.push({
        id: 'more',
        icon: 'moreWidget',
        labelKey: 'common_more',
        action: () => this.togglePremiumFeatures(),
        visible: true,
        isPremium: false
      });
    }

    // Secondary items - Flutter order: Habit Setup, Journal Setup, Money Tracker Setup, Calendar
    // Note: Focus Setup removed (Flutter doesn't have it)
    if (this.showAllFeatures()) {

      // Habit Setup - premium feature
      items.push({
        id: 'habit',
        icon: 'habit',
        labelKey: 'common_setupHabit',
        action: () => this.openHabitTypeSelect(),
        visible: true,
        isPremium: true
      });

      // Journal Setup - premium feature
      items.push({
        id: 'journal',
        icon: 'journal',
        labelKey: 'common_setupJournal',
        action: () => this.openJournalSetup(),
        visible: true,
        isPremium: true
      });

      // Money Tracker Setup - premium feature
      items.push({
        id: 'moneyTrackerSetup',
        icon: 'moneyTracker',
        labelKey: 'common_setupMoneyTracker',
        action: () => this.openMoneyTrackerSetup(),
        visible: true,
        isPremium: true
      });

      // Calendar - premium feature
      items.push({
        id: 'calendar',
        icon: 'calendar',
        labelKey: 'common_connectCalendar',
        action: () => this.openCalendarTypeSelect(),
        visible: true,
        isPremium: true
      });

    }

    return items.filter(item => item.visible);
  });

  /**
   * Toggle visibility of premium features - only if not dragging
   */
  togglePremiumFeatures(event?: MouseEvent): void {
    // Prevent toggle if user was dragging
    if (this.hasDragged && event) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.showAllFeatures.update(v => !v);
  }

  /**
   * Handle click outside to close (TapRegion equivalent)
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Check if click was outside this component
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.clickOutside.emit();
    }
  }

  /**
   * Get label from translations
   */
  getLabel(labelKey: string): string {
    return this.ss.t(labelKey) || labelKey;
  }

  /**
   * Handle item click - only trigger if not dragging
   */
  onItemClick(item: AddEntityItem, event: MouseEvent): void {
    // Prevent click if user was dragging
    if (this.hasDragged) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    item.action();
    this.itemSelected.emit();
  }

  // Action methods (same as entity.component.ts)
  private openTodo(): void {
    const dateString = getDateString();
    this.featureService.openTodoForm(null, dateString, true);
  }

  private openList(): void {
    this.featureService.openListForm(null, true);
  }

  private openNote(): void {
    this.featureService.openNoteForm(null, true);
  }

  private openFocusTrainer(): void {
    this.featureService.openFocusSetupSelect();
  }

  private openHabitSelect(): void {
    this.featureService.openHabitSetupSelect();
  }

  private openJournalSelect(): void {
    this.featureService.openJournalSetupSelect();
  }

  private openMoneyTrackerSelect(): void {
    this.featureService.openMoneyTrackerSetupSelect();
  }

  private openMoneyTracker(transactionType: 'income' | 'expense'): void {
    this.featureService.openMoneyTransactionForm(transactionType, null, true);
  }

  private openCalendarTypeSelect(): void {
    this.featureService.openCalendarTypeSelect();
  }

  private openMoneyTrackerSetup(): void {
    this.featureService.openMoneyTrackerSetupForm(null, true);
  }

  private openJournalSetup(): void {
    this.featureService.openJournalSetupForm(null, true);
  }

  private openHabitTypeSelect(): void {
    this.featureService.openHabitTypeSelect();
  }

  /**
   * Mouse drag scrolling - start dragging
   */
  onMouseDown(event: MouseEvent): void {
    if (!this.scrollContainer) return;

    this.isDragging = true;
    this.hasDragged = false; // Reset drag flag
    this.startX = event.pageX - this.scrollContainer.nativeElement.offsetLeft;
    this.scrollLeft = this.scrollContainer.nativeElement.scrollLeft;

    this.scrollContainer.nativeElement.style.userSelect = 'none';

    event.preventDefault();
  }

  /**
   * Mouse drag scrolling - stop dragging
   */
  onMouseUp(): void {
    if (!this.scrollContainer) return;

    this.isDragging = false;
    this.scrollContainer.nativeElement.style.userSelect = 'auto';
  }

  /**
   * Mouse drag scrolling - handle drag movement
   */
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging || !this.scrollContainer) return;

    event.preventDefault();

    const x = event.pageX - this.scrollContainer.nativeElement.offsetLeft;
    const walk = (x - this.startX) * 2; // Multiply for faster scroll

    // If moved more than 5px, consider it a drag (not a click)
    if (Math.abs(walk) > 5) {
      this.hasDragged = true;
    }

    this.scrollContainer.nativeElement.scrollLeft = this.scrollLeft - walk;
  }

  /**
   * Mouse drag scrolling - handle mouse leaving container
   */
  onMouseLeave(): void {
    if (this.isDragging && this.scrollContainer) {
      this.isDragging = false;
      this.scrollContainer.nativeElement.style.userSelect = 'auto';
    }
  }
}
